import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Subject } from 'rxjs';

import { PrechatFields } from '../interfaces';

/*
As the SalesForce chat is provided as an external JavaScript snippet and instructed to be loaded with a <script> tag,
we load it by injecting it to the document head.
*/

type Scripts = Record<
  string,
  {
    loaded: boolean;
  }
>;

interface HTMLScriptElementWithReady extends HTMLScriptElement {
  readyState: 'loaded' | 'complete';
  onreadystatechange: unknown;
}

@Injectable()
export class SalesforceChatService {
  private document = inject<Document>(DOCUMENT);

  private scripts: Scripts = {};
  public openChat$: Subject<PrechatFields> = new Subject<PrechatFields>();

  load(...scripts: string[]): Promise<unknown[]> {
    const promises: unknown[] = [];
    scripts.forEach((script) => promises.push(this.loadScript(script)));
    return Promise.all(promises);
  }

  private loadScript(src: string) {
    return new Promise((resolve, reject) => {
      if (!this.scripts[src]) {
        if (document.querySelector(`script[src="${src}"]`)) {
          this.scripts[src] = { loaded: true };
        } else {
          this.scripts[src] = { loaded: false };
        }
      }

      if (!this.scripts[src].loaded) {
        const script: HTMLScriptElementWithReady = this.document.createElement('script') as HTMLScriptElementWithReady;
        script.type = 'text/javascript';
        script.src = src;

        const resolveLoaded = (srcUrl: string) => {
          this.scripts[srcUrl].loaded = true;
          resolve({ src: srcUrl, loaded: true });
        };

        if (script.readyState) {
          script.onreadystatechange = () => {
            if (script.readyState === 'loaded' || script.readyState === 'complete') {
              script.onreadystatechange = null;
              resolveLoaded(src);
            }
          };
        } else {
          script.onload = () => {
            resolveLoaded(src);
          };
        }
        script.onerror = (_error: unknown) => {
          reject({ src, loaded: false });
        };
        document.getElementsByTagName('head')[0].appendChild(script);
      } else {
        resolve({ src, loaded: true });
      }
    });
  }
}
